const ZOOM_PING_ABORT_WINDOW_MS = 2000;
const ZOOM_PING_ABORT_GUARD_VERSION = 5;
const MAX_DIAGNOSTIC_ENTRIES = 50;

const isExpectedZoomAbort = (reason) => {
  const name = String(reason?.name || '');
  const message = String(reason?.message || reason || '').trim();
  return (
    name === 'AbortError' &&
    /^(?:signal is aborted without reason|this operation was aborted|the operation was aborted)\.?$/i.test(
      message,
    )
  );
};

const getZoomPingRequest = (input) => {
  try {
    const url = new URL(String(input?.url || input || ''));
    if (url.protocol !== 'https:' || !/^\/wc\/ping\//i.test(url.pathname)) {
      return null;
    }
    // Do not expose the meeting number or auth query parameters in diagnostics.
    return { host: url.host, path: '/wc/ping/[redacted]' };
  } catch {
    return null;
  }
};

const getRequestSignal = (input, init) => init?.signal || input?.signal;

const describeReason = (reason) => ({
  name: String(reason?.name || ''),
  message: String(reason?.message || reason || ''),
  code: reason?.code,
  stack: typeof reason?.stack === 'string' ? reason.stack : undefined,
});

const findExpectedAbortReason = (value, depth = 0, seen = new WeakSet()) => {
  if (isExpectedZoomAbort(value)) return value;
  if (!value || typeof value !== 'object' || depth >= 3 || seen.has(value)) {
    return null;
  }

  seen.add(value);
  for (const key of ['error', 'reason', 'cause', 'es']) {
    try {
      const match = findExpectedAbortReason(value[key], depth + 1, seen);
      if (match) return match;
    } catch {}
  }
  return null;
};

const isRecentZoomPingAbort = (state) =>
  Date.now() - state.lastAbortAt <= ZOOM_PING_ABORT_WINDOW_MS;

export const installZoomPingAbortGuard = () => {
  if (
    typeof window === 'undefined' ||
    window.__zoomPingAbortGuardVersion === ZOOM_PING_ABORT_GUARD_VERSION
  ) {
    return;
  }

  const state = {
    lastAbortAt: 0,
    observedSignals: new WeakSet(),
  };
  const reportDiagnostic = (type, details) => {
    if (process.env.NODE_ENV === 'production') return;

    const entry = {
      timestamp: new Date().toISOString(),
      type,
      ...details,
    };
    const diagnostics = Array.isArray(window.__zoomPingAbortDiagnostics)
      ? window.__zoomPingAbortDiagnostics
      : [];
    diagnostics.push(entry);
    if (diagnostics.length > MAX_DIAGNOSTIC_ENTRIES) diagnostics.shift();
    window.__zoomPingAbortDiagnostics = diagnostics;
    console.warn('[ZoomPingAbortGuard]', entry);
  };
  const recordZoomPingAbort = (signal, request) => {
    state.lastAbortAt = Date.now();
    reportDiagnostic('zoom-ping-signal-aborted', {
      request,
      reason: describeReason(signal.reason),
      abortCallStack: new Error('Zoom ping signal abort observed').stack,
    });
  };
  const observeZoomPingSignal = (signal, request) => {
    if (!signal || state.observedSignals.has(signal)) return;
    state.observedSignals.add(signal);
    reportDiagnostic('zoom-ping-signal-observed', {
      request,
      alreadyAborted: signal.aborted,
    });
    if (signal.aborted) {
      recordZoomPingAbort(signal, request);
      return;
    }
    signal.addEventListener(
      'abort',
      () => recordZoomPingAbort(signal, request),
      { once: true },
    );
  };
  const suppressExpectedZoomPingAbort = (event) => {
    const reason = event?.reason || event?.error;
    if (!isExpectedZoomAbort(reason)) return;

    const correlatedWithZoomPing = isRecentZoomPingAbort(state);
    reportDiagnostic('runtime-abort-event', {
      eventType: event.type,
      correlatedWithZoomPing,
      millisecondsSinceZoomPingAbort: Date.now() - state.lastAbortAt,
      reason: describeReason(reason),
    });
    if (!correlatedWithZoomPing) return;

    event.preventDefault();
    // Next's dev overlay registers a normal window listener before _app loads
    // and does not check defaultPrevented. A capture listener reaches the event
    // first; stopping propagation keeps this expected SDK cancellation out of
    // the overlay without hiding unrelated errors.
    event.stopImmediatePropagation();
  };

  reportDiagnostic('guard-installed', {
    version: ZOOM_PING_ABORT_GUARD_VERSION,
  });

  window.addEventListener(
    'unhandledrejection',
    suppressExpectedZoomPingAbort,
    true,
  );
  window.addEventListener('error', suppressExpectedZoomPingAbort, true);

  const nextConsoleError = window.console.error.bind(window.console);
  window.console.error = (...args) => {
    const reason = args.reduce(
      (match, value) => match || findExpectedAbortReason(value),
      null,
    );
    if (reason) {
      const correlatedWithZoomPing = isRecentZoomPingAbort(state);
      reportDiagnostic('console-error-with-abort', {
        correlatedWithZoomPing,
        millisecondsSinceZoomPingAbort: Date.now() - state.lastAbortAt,
        reason: describeReason(reason),
      });
      if (correlatedWithZoomPing) return;
    }
    nextConsoleError(...args);
  };

  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const zoomPingRequest = getZoomPingRequest(input);
    let fetchInit = init;
    if (zoomPingRequest) {
      observeZoomPingSignal(getRequestSignal(input, init), zoomPingRequest);

      // Video SDK 2.4.x aborts its shared ping controller after Promise.any
      // chooses a regional endpoint. In development, Next treats the losing
      // fetch rejections as runtime errors even though the SDK expects them.
      // Let these small probe requests finish instead of passing the SDK's
      // signal to fetch; production keeps Zoom's normal cancellation behavior.
      if (process.env.NODE_ENV === 'development' && init?.signal) {
        fetchInit = { ...init };
        delete fetchInit.signal;
        reportDiagnostic('zoom-ping-fetch-signal-detached', {
          request: zoomPingRequest,
        });
      }
    }

    try {
      return await nativeFetch(input, fetchInit);
    } catch (error) {
      // Zoom races regional connectivity probes and aborts the losing requests.
      // Convert only those expected cancellations into a response the SDK can
      // handle without surfacing an unhandled AbortError.
      if (zoomPingRequest && isExpectedZoomAbort(error)) {
        reportDiagnostic('zoom-ping-fetch-abort-caught', {
          request: zoomPingRequest,
          reason: describeReason(error),
        });
        return new Response(null, {
          status: 499,
          statusText: 'Zoom connectivity probe cancelled',
        });
      }
      throw error;
    }
  };
  window.__zoomPingAbortGuardInstalled = true;
  window.__zoomPingAbortGuardVersion = ZOOM_PING_ABORT_GUARD_VERSION;
};
